import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import PropTypes from "prop-types";

// Material
import { makeStyles } from "@material-ui/core";
import Card from "@material-ui/core/Card";

// Config
import { mediaStatus } from "src/config/values";
import { useAppInsights } from "src/utils/telemetry/AppInsights";
import colors from "src/config/colors";
import styles from "src/config/styles";
import { snackbarTypes } from "src/config/snackbar";

// Actions
import { reloadMedia } from "src/state/statements/actions";
import { getMediaItem } from "src/state/statements/actions";
import { toggleSnack } from "src/state/snackbar/actions";

// Components
import Text from "../Text";
import MediaItemContent from "../MediaItem/MediaItemContent";
import StatementAuthor from "./StatementAuthor";
import { get } from "lodash-es";
import { parseFetchOptions } from "src/state/user/actions";
import { statementItemUrl } from "src/config/apiConfig";

const useStyles = makeStyles(theme => ({
  root: {
    border: styles.values.border,
    borderColor: colors.wildSand,
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    width: `calc(50% - ${theme.spacing(1)}px)`,
    marginBottom: theme.spacing(2),
    alignItems: "flex-start",
    gap: theme.spacing(2)
  },
  contentText: {
    fontSize: "14px",
    color: colors.riverBed
  },
  heading: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginBottom: theme.spacing(2.5),
    width: "100%"
  },
  avatar: {
    height: 36,
    width: 36,
    marginRight: theme.spacing(1)
  },
  headingInfo: {
    display: "flex",
    flexDirection: "column"
  },
  footer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center"
  },
  filename: {
    color: colors.lynch,
    marginRight: theme.spacing(1)
  }
}));

const StatementItem = ({ statement, onClick }) => {
  const { caseId } = useParams();
  const classes = useStyles();
  const dispatch = useDispatch();
  const { trackException } = useAppInsights();
  const [statementData, setStatementData] = useState(null);

  const handleGetReport = () => {
    dispatch(
      reloadMedia({
        caseId,
        onFail: () =>
          trackException(`Failed to get statements from case: ${caseId}`)
      })
    );
  };

  useEffect(() => {
    if (!statementData) {
      dispatch(
        parseFetchOptions(async options => {
          const response = await fetch(statementItemUrl(caseId, statement.id), {
            method: "GET",
            headers: options.headers
          }).catch(() => {
            // no-op
          });

          if (response && response.ok) {
            const res = await response.json();
            setStatementData(res);
          }
        })
      );
    }
  }, [caseId, dispatch, statement.id]);

  return (
    <Card component="article" variant="outlined" className={classes.root}>
      <StatementAuthor
        answeredDateTime={statement.answeredDateTime}
        authorId={statement.authorId}
      />
      <Text type="subtitle1Small" className={classes.description}>
        {statement.title}
      </Text>
      <MediaItemContent
        creationDateTime={statement.creationDateTime}
        filename={statement.filename}
        getImage={handleGetReport}
        imgSrc={get(statementData, "downloadURLs.thumbnailURL", "")}
        status={!statement.isReportAvailable ? mediaStatus.PROCESSING : ""}
        onClick={onClick}
        onOpenReupload={() => {}}
      />
      <div className={classes.footer}>
        <Text type="subtitle1Small" className={classes.filename}>
          {statement.filename}
        </Text>
      </div>
    </Card>
  );
};

StatementItem.propTypes = {
  statement: PropTypes.shape({
    authorId: PropTypes.string,
    id: PropTypes.string,
    answeredDateTime: PropTypes.string,
    creationDateTime: PropTypes.string,
    title: PropTypes.string,
    isReportAvailable: PropTypes.bool,
    filename: PropTypes.string
  }).isRequired,
  onClick: PropTypes.func.isRequired
};

export default React.memo(StatementItem);
